import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { BASE_SIZE, ARENA_SIZE } from '../../../utils/constants.js';
import { PALETTE, matte, colored, vary, seededRandom } from '../../Style.js';

// A team's camp: trodden ground, a timber fence, tents, a campfire and
// team-coloured banners at each corner.
export class Base {
  constructor(scene, position, color) {
    this.scene = scene;
    this.position = position;
    this.color = color;
    this.meshes = [];
    this.flags = [];
    this.fire = null;
    this.light = null;
    this.rand = seededRandom(position.x > 0 ? 11 : 7);
    this.create();
  }

  add(object) {
    this.scene.add(object);
    this.meshes.push(object);
    return object;
  }

  create() {
    // Calculate clipped base boundaries to stay within arena
    const arenaHalf = ARENA_SIZE / 2;
    const halfBaseWidth = BASE_SIZE / 2;
    const halfBaseDepth = (BASE_SIZE * 1.15) / 2; // 15% taller in Z direction

    // Calculate the actual bounds, clamped to arena edges
    const westEdge = Math.max(this.position.x - halfBaseWidth, -arenaHalf);
    const eastEdge = Math.min(this.position.x + halfBaseWidth, arenaHalf);
    const northEdge = Math.max(this.position.z - halfBaseDepth, -arenaHalf);
    const southEdge = Math.min(this.position.z + halfBaseDepth, arenaHalf);

    // Calculate actual width and depth after clamping
    const actualWidth = eastEdge - westEdge;
    const actualDepth = southEdge - northEdge;
    const centerX = (westEdge + eastEdge) / 2;
    const centerZ = (northEdge + southEdge) / 2;

    // Which way is the back of the camp (towards the arena wall)
    const back = this.position.x < 0 ? -1 : 1;
    const rand = this.rand;
    const team = new THREE.Color(this.color);

    // Packed earth with a wash of team colour
    const earth = new THREE.Mesh(
      new THREE.PlaneGeometry(actualWidth, actualDepth),
      matte(PALETTE.dirt)
    );
    earth.rotation.x = -Math.PI / 2;
    earth.position.set(centerX, 0.02, centerZ);
    earth.receiveShadow = true;
    this.add(earth);

    const wash = new THREE.Mesh(
      new THREE.PlaneGeometry(actualWidth - 2, actualDepth - 2),
      new THREE.MeshStandardMaterial({ color: this.color, transparent: true, opacity: 0.22, roughness: 1, depthWrite: false })
    );
    wash.rotation.x = -Math.PI / 2;
    wash.position.set(centerX, 0.04, centerZ);
    wash.receiveShadow = true;
    this.add(wash);

    // A glowing team-coloured edge marks the camp boundary at a glance
    const edgeMaterial = this.edgeMaterial = new THREE.MeshStandardMaterial({
      color: this.color,
      emissive: this.color,
      emissiveIntensity: 0.7,
      roughness: 0.6
    });
    const edgeWidth = 0.7;
    for (const [x, z, w, d] of [
      [centerX, northEdge, actualWidth, edgeWidth],
      [centerX, southEdge, actualWidth, edgeWidth],
      [westEdge, centerZ, edgeWidth, actualDepth],
      [eastEdge, centerZ, edgeWidth, actualDepth]
    ]) {
      const edge = new THREE.Mesh(new THREE.BoxGeometry(w, 0.08, d), edgeMaterial);
      edge.position.set(x, 0.06, z);
      edge.receiveShadow = true;
      this.add(edge);
    }

    const parts = [];

    // Timber fence around the camp: posts and two rails, open on the arena side
    const fenceSides = [
      { from: [westEdge, northEdge], to: [eastEdge, northEdge] },
      { from: [westEdge, southEdge], to: [eastEdge, southEdge] },
      back < 0
        ? { from: [westEdge, northEdge], to: [westEdge, southEdge] }
        : { from: [eastEdge, northEdge], to: [eastEdge, southEdge] }
    ];
    for (const { from, to } of fenceSides) {
      const dx = to[0] - from[0];
      const dz = to[1] - from[1];
      const length = Math.hypot(dx, dz);
      const angle = Math.atan2(dx, dz);
      const posts = Math.ceil(length / 2.5);
      for (let i = 0; i <= posts; i++) {
        const t = i / posts;
        parts.push(colored(new THREE.CylinderGeometry(0.14, 0.17, 1.3, 5), vary(PALETTE.wood, rand, 0.12), {
          x: from[0] + dx * t, y: 0.65, z: from[1] + dz * t
        }));
        // Team-coloured cloth wrapped round the top of each post
        parts.push(colored(new THREE.CylinderGeometry(0.2, 0.2, 0.35, 5), team, {
          x: from[0] + dx * t, y: 1.15, z: from[1] + dz * t
        }));
      }
      for (const railY of [0.5, 1.0]) {
        parts.push(colored(new THREE.BoxGeometry(0.1, 0.12, length), PALETTE.woodLight, {
          x: from[0] + dx / 2, y: railY, z: from[1] + dz / 2, ry: angle
        }));
      }
    }

    // Two tents at the back of the camp, flaps facing the field
    const tentX = centerX + back * (actualWidth / 2 - 4);
    for (const side of [-1, 1]) {
      const tz = centerZ + side * (actualDepth / 2 - 4.5);
      const canvas = vary(PALETTE.cloth, rand, 0.08);
      // A triangular prism lying along X, apex up: an A-frame tent
      const tent = new THREE.CylinderGeometry(1.6, 1.6, 3, 3);
      parts.push(colored(tent, canvas, { x: tentX, y: 0.8, z: tz, rx: -Math.PI / 2, ry: Math.PI / 2 }));
      parts.push(colored(new THREE.BoxGeometry(3.2, 0.2, 0.3), team, { x: tentX, y: 2.42, z: tz }));
    }

    // Supplies stacked beside the tents
    for (let i = 0; i < 3; i++) {
      parts.push(colored(new THREE.BoxGeometry(1, 0.9, 1), vary(PALETTE.woodLight, rand, 0.12), {
        x: tentX - back * 2.2, y: 0.45 + (i === 2 ? 0.9 : 0), z: centerZ + (i === 1 ? 1.1 : 0) + (i === 2 ? 0.5 : 0), ry: rand() * 0.4
      }));
    }

    // Campfire ring between the tents
    const fireX = tentX;
    const fireZ = centerZ;
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2;
      parts.push(colored(new THREE.DodecahedronGeometry(0.28, 0), vary(PALETTE.stone, rand, 0.15), {
        x: fireX + Math.cos(a) * 1.0, y: 0.15, z: fireZ + Math.sin(a) * 1.0
      }));
    }
    for (let i = 0; i < 3; i++) {
      parts.push(colored(new THREE.CylinderGeometry(0.1, 0.1, 1.4, 5), PALETTE.woodDark, {
        x: fireX, y: 0.25, z: fireZ, rz: Math.PI / 2.4, ry: (i / 3) * Math.PI * 2
      }));
    }

    // Banner poles at the four corners
    const corners = [
      [westEdge, northEdge], [eastEdge, northEdge],
      [westEdge, southEdge], [eastEdge, southEdge]
    ];
    for (const [x, z] of corners) {
      parts.push(colored(new THREE.CylinderGeometry(0.12, 0.16, 6, 6), PALETTE.woodDark, { x, y: 3, z }));
      parts.push(colored(new THREE.SphereGeometry(0.22, 6, 4), 0xd9b25a, { x, y: 6.1, z }));
      this.createFlag(x, z);
    }

    const props = new THREE.Mesh(mergeGeometries(parts), matte(0xffffff, { vertexColors: true }));
    props.castShadow = true;
    props.receiveShadow = true;
    this.add(props);

    // Fire: flickering emissive flames and a warm light that matters most at night
    this.fire = new THREE.Group();
    const flameOuter = new THREE.Mesh(new THREE.ConeGeometry(0.55, 1.4, 6), new THREE.MeshBasicMaterial({ color: 0xff7a26, transparent: true, opacity: 0.85 }));
    const flameInner = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.9, 5), new THREE.MeshBasicMaterial({ color: 0xffe08a }));
    flameOuter.position.y = 0.75;
    flameInner.position.y = 0.5;
    this.fire.add(flameOuter, flameInner);
    this.fire.position.set(fireX, 0, fireZ);
    this.add(this.fire);

    this.light = new THREE.PointLight(0xff9a48, 20, 30, 1.6);
    this.light.position.set(fireX, 2.2, fireZ);
    this.add(this.light);
  }

  // A cloth banner whose vertices ripple in the wind each frame.
  createFlag(x, z) {
    const geometry = new THREE.PlaneGeometry(2.2, 1.3, 8, 3);
    geometry.translate(1.1, 0, 0);
    const material = new THREE.MeshStandardMaterial({ color: this.color, side: THREE.DoubleSide, roughness: 0.9, flatShading: true });
    const flag = new THREE.Mesh(geometry, material);
    flag.position.set(x, 5.2, z);
    // Fly the flags away from the arena centre line
    flag.rotation.y = this.position.x < 0 ? Math.PI : 0;
    flag.castShadow = true;
    flag.userData.base = Float32Array.from(geometry.attributes.position.array);
    flag.userData.phase = x * 0.3 + z * 0.2;
    this.add(flag);
    this.flags.push(flag);
  }

  update(elapsed, day) {
    for (const flag of this.flags) {
      const pos = flag.geometry.attributes.position;
      const base = flag.userData.base;
      for (let i = 0; i < pos.count; i++) {
        const bx = base[i * 3];
        pos.setZ(i, Math.sin(elapsed * 4 + bx * 2 + flag.userData.phase) * 0.18 * bx);
      }
      pos.needsUpdate = true;
      flag.geometry.computeVertexNormals();
    }

    const flicker = 1 + Math.sin(elapsed * 11) * 0.1 + Math.sin(elapsed * 17.3) * 0.08;
    this.fire.scale.set(1, flicker, 1);
    this.fire.rotation.y = elapsed * 2;
    const night = day ? day.night : 0;
    this.light.intensity = (15 + night * 45) * flicker;
  }

  remove() {
    this.meshes.forEach(mesh => {
      this.scene.remove(mesh);
      mesh.traverse?.(child => {
        if (child.isMesh) child.geometry.dispose();
      });
    });
    this.meshes = [];
    this.flags = [];
    this.edgeMaterial?.dispose();
  }
}
